import { RowNode } from '@ag-grid-community/core';
import { initial, last } from 'lodash';

import { CallApiWithNotification } from 'src/clients/ApiService';
import FilesClient, {
  CreateChannelData,
  FileBundleData,
} from 'src/clients/FilesClient';
import { FileErrorCode } from 'src/constants/errorConsts/errorCodesConsts';
import {
  FILE_ACCESS_LEVEL,
  FILE_TYPE,
  FOLDER_FILE_KEY,
} from 'src/constants/fileConsts';
import { Client } from 'src/store/clients/types';
import {
  AddFileChannelsActionOptions,
  AddFileChannelsActionType,
  DeleteFileChannelActionType,
  DownloadFileModel,
  DuplicateBehavior,
  DuplicateConflictState,
  FileAccessLevel,
  FileChannel,
  FileFieldsUpdateSuccessPayload,
  FileResponse,
  FilesActionTypes,
  FileSaveModel,
  FilesTableFormData,
  RemoveFileChannelsActionType,
  UnselectFileChannelOptions,
  UpdateFileChannelsActionType,
} from 'src/store/files/types';
import { AppThunkAction } from 'src/store/reduxTypes';
import {
  alertSnackbar,
  clearAlertMessage,
  toggleSecondarySidebar,
} from 'src/store/ui/actions';
import { API } from 'src/utils/AmplifyApiUtils';
import ApiUtils from 'src/utils/ApiUtils';
import { ensureApiError } from 'src/utils/Errors';
import FileUtils, { pathMatch } from 'src/utils/FileUtils';
import S3Utils from 'src/utils/S3Utils';

// Helper to check if an error is a duplicate file conflict
const isDuplicateConflictError = (error: unknown): boolean => {
  const err = error as {
    response?: {
      code?: string;
      data?: { code?: string };
      status?: number;
    };
    message?: string;
    code?: string;
  };

  // Check various error structures from Amplify API
  const errorCode =
    err?.response?.code || err?.response?.data?.code || err?.code;

  // Check if the error code matches
  if (errorCode === FileErrorCode.fileDuplicateConflict) {
    return true;
  }

  // Also check the message string as fallback (in case error was re-thrown)
  const errorMessage = err?.message || '';
  return (
    errorMessage.includes('file_duplicate_conflict') ||
    errorMessage.includes('file with this name already exists')
  );
};

// Action creator for duplicate conflict detected
export const duplicateConflictDetected = (payload: DuplicateConflictState) => ({
  type: FilesActionTypes.DUPLICATE_CONFLICT_DETECTED,
  payload,
});

// Action creator for clearing duplicate conflict
export const clearDuplicateConflict = () => ({
  type: FilesActionTypes.CLEAR_DUPLICATE_CONFLICT,
});

export const selectRow = (rowId: string) => ({
  type: FilesActionTypes.SELECT_ROW,
  payload: rowId,
});

export const deselectRow = (rowId: string) => ({
  type: FilesActionTypes.DESELECT_ROW,
  payload: rowId,
});

export const toggleRowSelection = (rowId: string) => ({
  type: FilesActionTypes.TOGGLE_ROW_SELECTION,
  payload: rowId,
});

export const selectAll = (rowIds: string[]) => ({
  type: FilesActionTypes.SELECT_ALL_ROWS,
  payload: rowIds,
});

export const clearSelection = () => ({
  type: FilesActionTypes.CLEAR_ROW_SELECTION,
});

export const getFilesForChannel =
  (fileChannelID: string): AppThunkAction =>
  async (dispatch) => {
    function request() {
      return { type: FilesActionTypes.GET_FILES };
    }

    function success(res: FileResponse[]) {
      return { type: FilesActionTypes.GET_FILES_SUCCESS, payload: res };
    }

    function error(err: string) {
      return { type: FilesActionTypes.GET_FILES_ERROR, payload: err };
    }

    dispatch(request());

    try {
      const res = await FilesClient.GetFilesForChannelId(fileChannelID);
      dispatch(success(res));
    } catch (err) {
      const { message } = ensureApiError(err);
      dispatch(error(message));
    }
  };

export const saveUploadedFiles =
  (
    files: Array<FileSaveModel>,
    fileChannelID: string,
    duplicateBehavior?: DuplicateBehavior,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    if (files.length === 0) {
      dispatch(
        alertSnackbar({
          errorMessage: 'Upload was not complete. Please refresh and try again',
        }),
      );
      return;
    }
    function request() {
      return { type: FilesActionTypes.SAVE_UPLOADED_FILES };
    }

    function success(
      res: FileResponse[],
      behavior?: DuplicateBehavior,
      replacedFolderPaths?: string[],
      replacedFilePaths?: Array<{ path: string; fileName: string }>,
    ) {
      return {
        type: FilesActionTypes.SAVE_UPLOADED_FILES_SUCCESS,
        payload: res,
        duplicateBehavior: behavior,
        replacedFolderPaths,
        replacedFilePaths,
      };
    }

    function error(err: string) {
      return { type: FilesActionTypes.SAVE_UPLOADED_FILES_ERROR, payload: err };
    }

    dispatch(request());

    try {
      const res = await FilesClient.CreateFilesInChannel(
        fileChannelID,
        files,
        duplicateBehavior,
      );

      // res contains the result of a batch operation
      // so we check if the batch creation was a success and throw a error if not
      if (ApiUtils.IsBatchCreateResultSuccessful(res)) {
        const state = getState();
        const creatorId = state.user.data?.userId;
        // UserState does not contain profile information, so we pull out our user
        // from the internal users array (for uis) and the users array (for clients)
        const allUsers = state.users.internalUsers.concat(state.users.users);
        const userInstance = allUsers.find((u) => u.id === creatorId);

        const mapResponse: FileResponse[] = res.createdItems.map(
          (file): FileResponse => {
            return {
              ...file,
              owner: userInstance || null,
            };
          },
        );

        // When replacing (duplicate_file_name feature flag enabled), identify root folder
        // paths that need to have their old content removed from state.
        // This is only set when duplicateBehavior is 'replace' (user chose to replace duplicates).
        let replacedFolderPaths: string[] | undefined;
        let replacedFilePaths:
          | Array<{ path: string; fileName: string }>
          | undefined;
        if (duplicateBehavior === 'replace') {
          // Get all folder paths from the uploaded files
          const folderPaths = files
            .filter((file) => file.key === FOLDER_FILE_KEY)
            .map((file) => file.path);

          // Find root folders (those not nested under another folder in this upload)
          replacedFolderPaths = folderPaths.filter(
            (path) =>
              !folderPaths.some(
                (otherPath) =>
                  otherPath !== path && path.startsWith(otherPath + '/'),
              ),
          );

          // Get all file paths (non-folders) that are being replaced
          // Files are identified by their path + fileName combination
          replacedFilePaths = files
            .filter((file) => file.key !== FOLDER_FILE_KEY)
            .map((file) => ({
              path: file.path,
              // Extract file name from the key (e.g., "files/timestamp/document.pdf" -> "document.pdf")
              fileName: file.key.split('/').pop() || '',
            }))
            .filter((item) => item.fileName !== '');
        }

        dispatch(
          success(
            mapResponse,
            duplicateBehavior,
            replacedFolderPaths,
            replacedFilePaths,
          ),
        );
      } else {
        // Check if the response contains duplicate conflicts via unauthorizedIds
        // unauthorizedIds contains the identifiers of items that had actual duplicate conflicts:
        // - For folders: the folder path (e.g., "some_folder")
        // - For files: the fileKey (e.g., "files/timestamp/filename.ext")
        const duplicateConflictIds = res.unauthorizedIds || [];
        if (duplicateConflictIds.length > 0) {
          // Extract display name from the first duplicate identifier
          // Get the last path segment (folder name or filename)
          const firstDuplicateId = duplicateConflictIds.at(0) || 'item';
          const fileName = firstDuplicateId.split('/').pop() || 'item';

          // Count is simply the number of actual duplicates minus 1 (for the one shown)
          const additionalConflictCount =
            duplicateConflictIds.length > 1
              ? duplicateConflictIds.length - 1
              : 0;

          dispatch(
            duplicateConflictDetected({
              fileName,
              additionalConflictCount,
              files,
              channelId: fileChannelID,
              operation: 'create',
            }),
          );
          return;
        }

        // Fallback: Check if errors array contains duplicate conflict error code
        const duplicateErrors =
          res.errors?.filter(
            (err) => err.code === FileErrorCode.fileDuplicateConflict,
          ) || [];
        if (duplicateErrors.length > 0) {
          // Extract name from the first file (use path for folders since key is placeholder)
          const firstFile = files.at(0);
          const fileName = firstFile
            ? firstFile.key === FOLDER_FILE_KEY
              ? firstFile.path.split('/').pop() || 'item'
              : firstFile.key.split('/').pop() || 'item'
            : 'item';
          const additionalConflictCount =
            duplicateErrors.length > 1 ? duplicateErrors.length - 1 : 0;
          dispatch(
            duplicateConflictDetected({
              fileName,
              additionalConflictCount,
              files,
              channelId: fileChannelID,
              operation: 'create',
            }),
          );
          return;
        }
        throw new Error('Failed to create file');
      }
    } catch (ex) {
      // Check if this is a duplicate conflict error (from thrown exceptions)
      if (isDuplicateConflictError(ex)) {
        // Extract name from the first file (use path for folders since key is placeholder)
        // Note: In exception case, we can't determine which specific files conflicted
        const firstFile = files.at(0);
        const fileName = firstFile
          ? firstFile.key === FOLDER_FILE_KEY
            ? firstFile.path.split('/').pop() || 'item'
            : firstFile.key.split('/').pop() || 'item'
          : 'item';
        dispatch(
          duplicateConflictDetected({
            fileName,
            files,
            channelId: fileChannelID,
            operation: 'create',
          }),
        );
        return;
      }

      const err = ensureApiError(ex);
      dispatch(error(err.message));
      dispatch(
        alertSnackbar({
          errorMessage: err.message,
        }),
      );
    }
  };

export const filesDeletedAction = (fileIds: string[]) => ({
  type: FilesActionTypes.DELETE_FILES,
  payload: fileIds,
});

export const folderDeletedAction = (folderId: string) => ({
  type: FilesActionTypes.DELETE_FOLDER,
  payload: folderId,
});

export const clearFiles = (): AppThunkAction => async (dispatch) => {
  dispatch({
    type: FilesActionTypes.CLEAR_FILES,
  });
};

/**
 * Revert the selection of a file channel and remove its files from state
 * @param options a collection of configurations to customize the behaviors desired by this action
 */
export const unselectFileChannel =
  (options?: UnselectFileChannelOptions): AppThunkAction =>
  async (dispatch) => {
    dispatch({
      type: FilesActionTypes.UNSELECT_CHANNEL,
    });
    if (options) {
      if (options.openSecondarySidebar) {
        dispatch(
          toggleSecondarySidebar({ isOpen: true, info: '', hidden: false }),
        );
      }
      if (options.navigationPath) {
        options.navigate(options.navigationPath);
      }
    }
  };

export const selectFileChannel = (channel?: FileChannel) => ({
  type: FilesActionTypes.SELECT_FILES_CHANNEL,
  payload: channel,
});

export const addFileChannelsAction = (
  channels: FileChannel[],
  addFileChannelOptions?: AddFileChannelsActionOptions,
): AddFileChannelsActionType => ({
  type: FilesActionTypes.ADD_FILE_CHANNELS,
  channels,
  addFileChannelOptions,
});

export const deleteFileChannelAction = (
  channelId: string,
): DeleteFileChannelActionType => ({
  type: FilesActionTypes.DELETE_FILE_CHANNEL,
  channelId,
});

export const updateFileChannelsAction = (
  updatedChannels: FileChannel[],
): UpdateFileChannelsActionType => ({
  type: FilesActionTypes.UPDATE_FILE_CHANNELS,
  updatedChannels,
});

export const removeFileChannelsAction = (
  channels: FileChannel[],
): RemoveFileChannelsActionType => ({
  type: FilesActionTypes.REMOVE_FILE_CHANNELS,
  channels,
});

export interface CreateFileChannelInput {
  companyID: string;
  clientUserIDs?: string[];
  clients?: Client[];
  membershipType: string;
}

/**
 * Create a file channel with given client users. If there is
 * a channel with matching members then it should be selected
 * otherwise new channel should be created
 * @param clientUserIds which client users are part of channel
 */
export const createFileChannel =
  (
    input: CreateFileChannelInput,
    setActiveChannel: (channelId: string, channel?: FileChannel) => void,
  ): AppThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      files: { channels: fileChannels },
    } = getState();
    const { companyID, clientUserIDs } = input;

    let existingChannel: FileChannel | undefined;
    // params to be passed to the API request if existing channel is not found
    const params: CreateChannelData = {
      clientUserIDs: clientUserIDs || [],
      companyID,
      membershipType: input.membershipType,
    };
    if (input.membershipType === 'company') {
      // if a companyID is passed into the create channel function check for a company channel matching this company
      existingChannel = fileChannels.find(
        (chan) =>
          chan.id.endsWith(companyID) && chan.membershipType === 'company',
      );
    } else if (clientUserIDs) {
      const clientsList = (input.clients || []).map((c) => c.id);
      // if every given client userId is included in a channel with the same companyId then this channel already exists
      existingChannel = fileChannels.find(
        (chan) =>
          chan.fields.companyID === companyID &&
          chan.membershipType === input.membershipType &&
          clientUserIDs.every(
            (clientUserId) => chan.additionalFields?.memberIDs[clientUserId],
          ) &&
          Object.keys(chan.additionalFields?.memberIDs || {}) // get the memberIds as an array
            .filter((memberID) => clientsList.includes(memberID)) // filter the memberIds to only include the clients in the input
            .every((clientMemberID) => clientUserIDs.includes(clientMemberID)), // check all members are in the input list of selected clients we are creating the channel for
      );
      if (clientUserIDs.length === 1) {
        params.singleClientId = clientUserIDs.at(0);
      }
    }

    if (!existingChannel) {
      const result = await CallApiWithNotification({
        executeFunction: FilesClient.createChannel,
        checkFunction: ApiUtils.IsBatchCreateResultSuccessful,
        params,
        successMessage: `Channel created.`,
        errorMessage: `There was an problem creating this channel.`,
        dispatch,
      });

      if (result.status) {
        dispatch(
          addFileChannelsAction(result.data.createdItems, {
            selectFirstChannel: true,
          }),
        );
        setActiveChannel(
          result.data.createdItems[0].id,
          result.data.createdItems[0],
        );
      } else {
        console.error('Unsuccessful file channel create result', result);
      }
    } else {
      // select the channel that was found
      dispatch(selectFileChannel(existingChannel));
      setActiveChannel(existingChannel.id, existingChannel);
    }
  };

/**
 * Deletes file channel
 * @param channelId channel id which needs to be deleted
 */
export const deleteFileChannel =
  (channelId: string): AppThunkAction =>
  async (dispatch, getState) => {
    const { channels } = getState().files;
    console.info('Deleting file channel with id', channelId);

    const result = await CallApiWithNotification({
      executeFunction: FilesClient.deleteFileChannel,
      checkFunction: ApiUtils.IsBatchUpdateResultSuccessful,
      params: channelId,
      successMessage: `Channel deleted`,
      errorMessage: `There was a problem deleting this channel.`,
      dispatch,
    });

    if (result.status && result.data.succeededIds.length > 0) {
      dispatch(deleteFileChannelAction(result.data.succeededIds[0]));
      // after deleting a channel, select the next channel in the list
      const nextChannel = channels
        .filter((channel) => channel.id !== channelId)
        .at(0);
      dispatch(selectFileChannel(nextChannel));
    } else {
      console.error('Unsuccessful file channel create result', result);
    }
  };

export const downloadZip =
  (
    fileData: FileBundleData[],
    bundleName: string,
  ): AppThunkAction<Promise<string>> =>
  async (dispatch) => {
    try {
      const result = await CallApiWithNotification<string>({
        executeFunction: FilesClient.getZippedBundle,
        params: {
          fileData,
          bundleName,
        },
        successMessage: '',
        errorMessage: `Your download failed. Please try again or contact support.`,
        dispatch,
      });

      if (result.status) {
        return result.data;
      }
    } catch (error) {
      console.error(
        `Failed to create zip file on fileData: ${JSON.stringify(fileData)}`,
      );
    }
    return '';
  };

export const downloadFiles =
  (files: DownloadFileModel[]): AppThunkAction =>
  async (dispatch) => {
    function startDownLoadFile() {
      return {
        type: FilesActionTypes.DOWNLOAD_FILE,
      };
    }

    function downloadFileSuccess() {
      return {
        type: FilesActionTypes.DOWNLOAD_FILE_SUCCESS,
      };
    }

    function downloadFileFailed(error: string) {
      return { type: FilesActionTypes.DOWNLOAD_FILE_ERROR, payload: error };
    }

    dispatch(clearAlertMessage());
    dispatch(startDownLoadFile());
    dispatch(
      alertSnackbar({
        successMessage: `Your download has begun`,
      }),
    );

    try {
      await Promise.all(
        files.map(async (fileData) => {
          const { fileUrl, fileKey, fileName } = fileData;
          return FileUtils.downloadFileFromUrl(fileUrl, fileKey, fileName);
        }),
      );

      dispatch(downloadFileSuccess());

      files.forEach((file) => {
        if (!file.emitAuditEvent) return;
        API.post('AppAPI', '/events', {
          body: {
            eventType: 'fileDownloaded',
            eventDescription: 'Downloaded',
            context: {
              ...(file.id && { id: file.id }),
              fileKey: file.fileKey,
              name: file.fileName,
              ...(file.channelId && { channelId: file.channelId }),
              ...(file.path && { path: file.path }),
            },
          },
        }).catch(() => {
          // Intentionally silent — audit failures must not block downloads,
          // and the backend already monitors and alerts on these errors.
        });
      });
    } catch (e) {
      const { message } = ensureApiError(e);
      dispatch(downloadFileFailed(message));
      dispatch(
        alertSnackbar({
          errorMessage: `The file could not be downloaded.`,
        }),
      );
    }
  };

export const filesUpdatedAction =
  (
    successIds: string[],
    updatedFiles: Array<FileFieldsUpdateSuccessPayload>,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    const { files } = getState();
    const { items } = files;
    const currentURL = new URL(window.location.href);
    const selectedFilePath = currentURL.searchParams.get('selectedFilePath');
    if (selectedFilePath) {
      updatedFiles.forEach((updatedFile) => {
        if (updatedFile.fields.fileKey === FOLDER_FILE_KEY) {
          // if one of the updated files is a folder, we want to check if a user is currently viewing that folder
          const oldFolder = items.find((item) => item.id === updatedFile.id);
          if (oldFolder?.fields.path === selectedFilePath) {
            // if the updated folder's path matches what the user is viewing now, we want to navigate them to the new path to represent that folder
            currentURL.searchParams.set(
              'selectedFilePath',
              updatedFile.fields.path,
            );
            window.location.href = currentURL.toString();
          }
        }
      });
    }

    return dispatch({
      type: FilesActionTypes.UPDATE_FILE_FIELDS_SUCCESS,
      successIds,
      updatedFiles,
    });
  };

export const renameFile =
  (
    fileData: FilesTableFormData,
    existingPaths: string[],
    duplicateBehavior?: DuplicateBehavior,
    options?: { skipLocalDuplicateCheck?: boolean },
  ): AppThunkAction =>
  async (dispatch, getState) => {
    function request() {
      return { type: FilesActionTypes.UPDATE_FILE_FIELDS_REQUEST };
    }

    function failure(error: string) {
      return { type: FilesActionTypes.UPDATE_FILE_FIELDS_FAILURE, error };
    }

    dispatch(request());

    const { files } = getState();
    const { items, selectedChannel } = files;
    const originalPath = fileData.path.join('/');
    const updatedPath = initial(fileData.path)
      .concat([fileData.fields.fileName || ''])
      .join('/');
    const updatedPathParts = updatedPath.split('/');

    // Only do local duplicate check if not retrying with a duplicateBehavior
    // and if skipLocalDuplicateCheck is not set (when feature flag is enabled)
    if (
      !duplicateBehavior &&
      !options?.skipLocalDuplicateCheck &&
      fileData.fileKey === FOLDER_FILE_KEY &&
      existingPaths.some(
        (i) => i === updatedPathParts[updatedPathParts.length - 1],
      )
    ) {
      const errorMessage = 'A folder with this name already exists.';
      dispatch(
        alertSnackbar({
          errorMessage,
        }),
      );
      dispatch(failure(errorMessage));
      return;
    }

    const targetFiles = FileUtils.getAllFilesFromPath({
      files: items,
      sourcePath: fileData.filePath.join('/'),
      fileType: fileData.type,
      fileKey: fileData.fileKey,
    });
    const updatedFiles = targetFiles.map((targetFile) => ({
      id: targetFile.id,
      clientPermissions: targetFile.clientPermissions,
      fields: {
        ...targetFile.fields,
        fileName:
          fileData.type === FILE_TYPE.FILE
            ? fileData.fields.fileName || ''
            : targetFile.fields.fileName || '',
        fileKey: targetFile.fields.fileKey,
        starred: false,
        path:
          fileData.fileKey === FOLDER_FILE_KEY
            ? updatedPath +
              (targetFile.fields.path
                ? targetFile.fields.path.slice(originalPath.length)
                : '')
            : targetFile.fields.path,
      },
    }));

    const noun = fileData.type === FILE_TYPE.FILE ? 'File' : 'Folder';

    try {
      const res = await FilesClient.updateFiles(
        false,
        duplicateBehavior,
      )(updatedFiles);

      if (ApiUtils.IsBatchUpdateResultSuccessful(res)) {
        // When duplicateBehavior is 'replace', remove the replaced file/folder from state
        // The replaced item is any existing item at the target path + fileName (excluding the renamed item)
        if (duplicateBehavior === 'replace') {
          const renamedFileIds = new Set(updatedFiles.map((f) => f.id));
          // Check if it's a folder by fileKey, not by type (more reliable)
          const isFolder = fileData.fileKey === FOLDER_FILE_KEY;

          // Helper to normalize path - treat empty, undefined, and "/" as equivalent (root)
          const normalizePath = (p: string | undefined): string => {
            if (!p || p === '/') return '';
            return p;
          };

          if (!isFolder) {
            // For files/links: find existing file with same path + target fileName
            // Use fileData.path (the folder path from FilesTableData), not fileData.fields.path
            // fileData.fields only contains the NEW values being set (just fileName from rename form)
            const targetPath = normalizePath(fileData.path?.join('/'));
            const targetFileName = fileData.fields.fileName ?? '';

            const replacedFiles = items.filter((item) => {
              if (renamedFileIds.has(item.id)) return false;
              // Skip folders
              if (item.fields.fileKey === FOLDER_FILE_KEY) return false;

              // Get effective file name:
              // - For files: fields.fileName (if renamed) or fileKey basename
              // - For links: fields.fileName (if renamed) or fields.name
              const itemFileKeyBasename =
                item.fields.fileKey?.split('/').pop() ?? '';
              const itemFileName =
                item.fields.fileName ||
                (item.fields as { name?: string })?.name ||
                itemFileKeyBasename ||
                '';

              // Determine the folder path for this item
              // For links: path is always just the folder path
              // For files: path format is inconsistent - sometimes includes filename, sometimes not
              let itemFolderPath = '';
              if (item.fields.path) {
                const pathEndsWithFileKey =
                  itemFileKeyBasename &&
                  (item.fields.path === itemFileKeyBasename ||
                    item.fields.path.endsWith('/' + itemFileKeyBasename));

                if (pathEndsWithFileKey) {
                  // Path includes filename, extract folder portion
                  itemFolderPath = FileUtils.getEffectiveFolderPath(
                    item.fields.path,
                    itemFileKeyBasename,
                  );
                } else {
                  // Path is just the folder path (links, or files where path doesn't include filename)
                  itemFolderPath = item.fields.path;
                }
              }
              itemFolderPath = normalizePath(itemFolderPath);

              return (
                itemFolderPath === targetPath && itemFileName === targetFileName
              );
            });

            if (replacedFiles.length > 0) {
              dispatch(filesDeletedAction(replacedFiles.map((f) => f.id)));
            }
          } else {
            // For folders: find existing folder at the target path and all nested content
            // Use filesDeletedAction (not folderDeletedAction) to avoid incorrect matching
            // folderDeletedAction uses startsWith without '/' suffix which would incorrectly
            // remove sibling folders like "Folder A (1)" when deleting "Folder A"
            const folderPath = normalizePath(updatedPath);
            // Find all items to delete: the folder itself + all nested items
            const itemsToDelete = items.filter((item) => {
              if (renamedFileIds.has(item.id)) return false;
              const itemPath = normalizePath(item.fields.path);
              // Match the folder itself or any nested content (path starts with folderPath + '/')
              return (
                itemPath === folderPath || itemPath.startsWith(folderPath + '/')
              );
            });
            if (itemsToDelete.length > 0) {
              dispatch(filesDeletedAction(itemsToDelete.map((f) => f.id)));
            }
          }
        }

        // Use updatedItems from the response if available (contains actual updated data
        // from backend including resolved duplicate names like "file(1).txt")
        // Otherwise fall back to the pre-constructed updatedFiles
        const filesToDispatch =
          res.updatedItems && res.updatedItems.length > 0
            ? res.updatedItems.map((item) => ({
                id: item.id,
                clientPermissions: item.clientPermissions,
                fields: {
                  status: item.fields.status,
                  fileName: item.fields.fileName ?? '',
                  fileKey: item.fields.fileKey,
                  starred: item.fields.starred,
                  path: item.fields.path ?? '',
                  esignStatus: item.fields.esignStatus,
                  lastEsigModifierIdentityID:
                    item.fields.lastEsigModifierIdentityID,
                  components: item.fields.components,
                },
              }))
            : updatedFiles.map((file) => ({
                ...file,
                fields: { ...file.fields, path: file.fields.path ?? '' },
              }));

        dispatch(filesUpdatedAction(res.succeededIds, filesToDispatch));
        dispatch(
          alertSnackbar({ successMessage: `${noun} has been renamed.` }),
        );
      } else {
        // Check if the response contains a duplicate conflict error
        const hasDuplicateError = res.errors?.some(
          (err) => err.code === FileErrorCode.fileDuplicateConflict,
        );
        if (hasDuplicateError) {
          const fileName = fileData.fields.fileName || fileData.fileName;
          dispatch(
            duplicateConflictDetected({
              fileName,
              channelId: selectedChannel?.id || '',
              operation: 'rename',
              originalFileData: fileData,
              existingPaths,
            }),
          );
          return;
        }
        const errorMessage = `${noun} could not be renamed.`;
        dispatch(failure(errorMessage));
        dispatch(alertSnackbar({ errorMessage }));
      }
    } catch (ex) {
      // Check if this is a duplicate conflict error (from thrown exceptions)
      if (isDuplicateConflictError(ex)) {
        const fileName = fileData.fields.fileName || fileData.fileName;
        dispatch(
          duplicateConflictDetected({
            fileName,
            channelId: selectedChannel?.id || '',
            operation: 'rename',
            originalFileData: fileData,
            existingPaths,
          }),
        );
        return;
      }

      const err = ensureApiError(ex);
      dispatch(failure(err.message));
      dispatch(
        alertSnackbar({
          errorMessage: err.message,
        }),
      );
    }
  };

export const updateFolderPermissions =
  (fileId: string, clientPermissions: FileAccessLevel): AppThunkAction =>
  async (dispatch, getState) => {
    // Capture previous state for rollback
    const { files } = getState();
    const previousFiles = files.items;
    const targetFolder = files.items.find((file) => file.id === fileId);

    if (targetFolder) {
      const folderPath = targetFolder.fields.path || '';

      // Optimistically update the folder and all its descendants
      const updatedFiles = files.items.map((file) => {
        const filePath = file.fields.path || '';
        const isTargetFolder = file.id === fileId;
        const isDescendant =
          folderPath && filePath.startsWith(folderPath + '/');

        if (
          isTargetFolder ||
          (isDescendant && file.fields.fileKey === FOLDER_FILE_KEY)
        ) {
          return {
            ...file,
            clientPermissions,
          };
        }
        return file;
      });

      dispatch({
        type: FilesActionTypes.GET_FILES_SUCCESS,
        payload: updatedFiles,
      });
    }

    try {
      await CallApiWithNotification({
        executeFunction: (params: {
          fileId: string;
          clientPermissions: FileAccessLevel;
        }) =>
          FilesClient.updateFolderPermissions(
            params.fileId,
            params.clientPermissions,
          ),
        params: { fileId, clientPermissions },
        successMessage:
          clientPermissions === FILE_ACCESS_LEVEL.READ_ONLY
            ? 'Folder locked'
            : 'Folder unlocked',
        errorMessage: 'Failed to update folder permissions',
        dispatch,
      });
    } catch (error) {
      // Rollback on failure
      dispatch({
        type: FilesActionTypes.GET_FILES_SUCCESS,
        payload: previousFiles,
      });
    }
  };

export const updateRootFolderPermissions =
  (fileChannelID: string, clientPermissions: FileAccessLevel): AppThunkAction =>
  async (dispatch, getState) => {
    // Capture previous state for rollback
    const { files } = getState();
    const previousFiles = files.items;
    const previousChannel = files.channels.find((c) => c.id === fileChannelID);
    const previousRootPermissions = previousChannel?.rootClientPermissions;

    // Optimistically update all folders in the channel immediately
    const updatedFiles = files.items.map((file) => {
      if (file.fields.fileKey === FOLDER_FILE_KEY) {
        return { ...file, clientPermissions };
      }
      return file;
    });
    dispatch({
      type: FilesActionTypes.GET_FILES_SUCCESS,
      payload: updatedFiles,
    });

    // Update the file channel's rootClientPermissions
    dispatch({
      type: FilesActionTypes.UPDATE_FILE_CHANNEL,
      channelId: fileChannelID,
      updates: { rootClientPermissions: clientPermissions },
    });

    try {
      await CallApiWithNotification({
        executeFunction: (params: {
          fileChannelID: string;
          clientPermissions: FileAccessLevel;
        }) =>
          FilesClient.updateRootFolderPermissions(
            params.fileChannelID,
            params.clientPermissions,
          ),
        params: { fileChannelID, clientPermissions },
        successMessage:
          clientPermissions === FILE_ACCESS_LEVEL.READ_ONLY
            ? 'Root folder locked'
            : 'Root folder unlocked',
        errorMessage: 'Failed to update root folder permissions',
        dispatch,
      });
    } catch (error) {
      // Rollback on failure
      dispatch({
        type: FilesActionTypes.GET_FILES_SUCCESS,
        payload: previousFiles,
      });
      dispatch({
        type: FilesActionTypes.UPDATE_FILE_CHANNEL,
        channelId: fileChannelID,
        updates: { rootClientPermissions: previousRootPermissions },
      });
    }
  };

/**
 * determine the path to set on a file object during a folder move event
 * @param srcPath points to root path where files are being moved from
 * @param filePath path of the file, which has srcPath as prefix but can be longer
 * @param tgtPath ponts to the directory where files should be moved
 */
const getModifiedTargetPath = (
  srcPath: string,
  filePath: string,
  tgtPath: string,
) => {
  if (pathMatch(srcPath, tgtPath)) {
    // if files are moved to some higher node in the same tree, we need to slice out the "intermediary" portion
    // for example, if we are moving items with path "A/B/C" to "A", we need to cut out "/B" for each of them
    const sourceFolderName: string = last(srcPath.split('/')) || '';
    const deletionPath: string = filePath.slice(
      tgtPath.length,
      srcPath.length - sourceFolderName.length,
    );
    return `${tgtPath ? `${tgtPath}/` : ''}${filePath.slice(
      tgtPath.length + deletionPath.length,
    )}`;
  }
  // there may be an intermediary path to delete on the filepath when moving folders to other folders that are not in the same "tree"
  const deletionPath: string = srcPath.split('/').slice(0, -1).join('/');
  return `${tgtPath}${deletionPath.length > 0 ? '' : '/'}${filePath.slice(
    deletionPath.length,
  )}`
    .replace(/\/$/, '')
    .replace(/^\//, '');
};

export const moveFolders =
  (
    targetPath: string,
    sourcePath: string,
    fileType: string,
    fileKey: string,
    entityName: string,
    duplicateBehavior?: DuplicateBehavior,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    function request() {
      return { type: FilesActionTypes.MOVE_FOLDER_REQUEST };
    }

    function success(updatedFiles: FileResponse[]) {
      return {
        type: FilesActionTypes.MOVE_FOLDER_SUCCESS,
        payload: updatedFiles,
      };
    }

    function failure(error: string) {
      return { type: FilesActionTypes.MOVE_FOLDER_FAILURE, error };
    }

    dispatch(request());

    const { files } = getState();
    const { items, selectedChannel } = files;

    const movedFiles = FileUtils.getAllFilesFromPath({
      files: items,
      sourcePath,
      fileType,
      fileKey,
    });

    const updatedFiles = movedFiles.map((movedFile) => ({
      ...movedFile,
      fields: {
        ...movedFile.fields,
        path:
          fileType === FILE_TYPE.FILE
            ? targetPath
            : getModifiedTargetPath(
                sourcePath,
                movedFile?.fields?.path || '',
                targetPath,
              ),
      },
    }));

    try {
      const res = await FilesClient.updateFiles(
        true,
        duplicateBehavior,
      )(updatedFiles);

      if (ApiUtils.IsBatchUpdateResultSuccessful(res)) {
        const movedFileIds = new Set(movedFiles.map((f) => f.id));
        const isFolder = fileType === FILE_TYPE.FOLDER;

        // Helper to normalize path - treat empty, undefined, and "/" as equivalent (root)
        const normalizePath = (p: string | undefined): string => {
          if (!p || p === '/') return '';
          return p;
        };

        // When duplicateBehavior is 'replace', remove the replaced file/folder from state
        // The replaced item is any existing item at the target path + fileName (excluding the moved item)
        if (duplicateBehavior === 'replace') {
          if (!isFolder) {
            // For files/links: find existing file at targetPath with same name
            // Backend detects duplicates by fileKey basename for files, or by name field for links
            const [movedFile] = movedFiles;
            const movedFileKeyBasename =
              movedFile?.fields.fileKey?.split('/').pop() ?? '';
            // Get the effective name for comparison:
            // - For files: fileKey basename (e.g., "document.pdf")
            // - For links: fields.name or fields.fileName if renamed
            const movedName =
              movedFile?.fields.fileName ||
              (movedFile?.fields as { name?: string })?.name ||
              movedFileKeyBasename ||
              entityName;
            const normalizedTargetPath = normalizePath(targetPath);

            const replacedFiles = items.filter((item) => {
              if (movedFileIds.has(item.id)) return false;
              if (item.fields.fileKey === FOLDER_FILE_KEY) return false;

              // Get fileKey basename for comparison (for regular files)
              const itemFileKeyBasename =
                item.fields.fileKey?.split('/').pop() ?? '';
              // Get the effective name:
              // - For files: fileKey basename
              // - For links: fields.name or fields.fileName if renamed
              const itemName =
                item.fields.fileName ||
                (item.fields as { name?: string })?.name ||
                itemFileKeyBasename ||
                '';

              // Determine the folder path for this item
              // For links: path is always just the folder path
              // For files: path format is inconsistent - sometimes includes filename, sometimes not
              // We check if the path ends with the fileKey basename to determine which format
              let itemFolderPath = '';
              if (item.fields.path) {
                // Check if path ends with fileKey basename (only applies to files, not links)
                const pathEndsWithFileKey =
                  itemFileKeyBasename &&
                  (item.fields.path === itemFileKeyBasename ||
                    item.fields.path.endsWith('/' + itemFileKeyBasename));

                if (pathEndsWithFileKey) {
                  // Path includes filename, extract folder portion
                  itemFolderPath = FileUtils.getEffectiveFolderPath(
                    item.fields.path,
                    itemFileKeyBasename,
                  );
                } else {
                  // Path is just the folder path (links, or files where path doesn't include filename)
                  itemFolderPath = item.fields.path;
                }
              }
              itemFolderPath = normalizePath(itemFolderPath);

              // Match by name (fileKey basename for files, name field for links)
              return (
                itemFolderPath === normalizedTargetPath &&
                itemName === movedName
              );
            });

            if (replacedFiles.length > 0) {
              dispatch(filesDeletedAction(replacedFiles.map((f) => f.id)));
            }
          } else {
            // For folders: find existing folder at the target path and all nested content
            // The folder's name is the last segment of sourcePath
            const folderName = sourcePath.split('/').pop() ?? '';
            // After move, folder will be at: targetPath/folderName (or just folderName if targetPath is root)
            const newFolderPath = normalizePath(targetPath)
              ? `${normalizePath(targetPath)}/${folderName}`
              : folderName;

            // Find all items to delete: files/folders at the target folder path or nested under it
            const itemsToDelete = items.filter((item) => {
              if (movedFileIds.has(item.id)) return false;
              const itemPath = normalizePath(item.fields.path);
              // Match items at the folder path or nested under it
              return (
                itemPath === newFolderPath ||
                itemPath.startsWith(newFolderPath + '/')
              );
            });

            if (itemsToDelete.length > 0) {
              dispatch(filesDeletedAction(itemsToDelete.map((f) => f.id)));
            }
          }
        }

        // Use updatedItems from the response if available (contains actual updated data
        // from backend including resolved duplicate paths like "folder (1)" for append behavior)
        // Merge with original movedFiles to get complete FileResponse objects
        let filesToDispatch: FileResponse[];
        if (res.updatedItems && res.updatedItems.length > 0) {
          // Create a map of updated items by ID for quick lookup
          const updatedItemsMap = new Map(
            res.updatedItems.map((item) => [item.id, item]),
          );

          filesToDispatch = movedFiles.map((originalFile) => {
            const backendItem = updatedItemsMap.get(originalFile.id);
            if (backendItem) {
              // Merge backend response with original file, updating the path
              return {
                ...originalFile,
                fields: {
                  ...originalFile.fields,
                  path: backendItem.fields.path ?? '',
                  fileName:
                    backendItem.fields.fileName ?? originalFile.fields.fileName,
                },
                clientPermissions:
                  backendItem.clientPermissions ??
                  originalFile.clientPermissions,
              };
            }
            // Fallback to locally computed path if not in backend response
            return {
              ...originalFile,
              fields: {
                ...originalFile.fields,
                path:
                  fileType === FILE_TYPE.FILE
                    ? targetPath
                    : getModifiedTargetPath(
                        sourcePath,
                        originalFile?.fields?.path || '',
                        targetPath,
                      ),
              },
            };
          });
        } else {
          // Fallback to locally computed updatedFiles
          filesToDispatch = updatedFiles;
        }

        dispatch(success(filesToDispatch));
        dispatch(
          alertSnackbar({
            successMessage: `The ${entityName} has been moved.`,
          }),
        );
      } else {
        // Check if the response contains a duplicate conflict error
        const hasDuplicateError = res.errors?.some(
          (err) => err.code === FileErrorCode.fileDuplicateConflict,
        );
        if (hasDuplicateError) {
          dispatch(
            duplicateConflictDetected({
              fileName: entityName,
              channelId: selectedChannel?.id || '',
              operation: 'move',
              moveParams: {
                targetPath,
                sourcePath,
                fileType,
                fileKey,
                entityName,
              },
            }),
          );
          return;
        }
        const errorMessage = `This ${entityName} could not be moved.`;
        dispatch(failure(errorMessage));
        dispatch(alertSnackbar({ errorMessage }));
      }
    } catch (ex) {
      // Check if this is a duplicate conflict error (from thrown exceptions)
      if (isDuplicateConflictError(ex)) {
        dispatch(
          duplicateConflictDetected({
            fileName: entityName,
            channelId: selectedChannel?.id || '',
            operation: 'move',
            moveParams: {
              targetPath,
              sourcePath,
              fileType,
              fileKey,
              entityName,
            },
          }),
        );
        return;
      }

      const err = ensureApiError(ex);
      dispatch(failure(err.message));
      dispatch(
        alertSnackbar({
          errorMessage: err.message,
        }),
      );
    }
  };

export const setFileChannelsAction = (channels: FileChannel[]) => ({
  type: FilesActionTypes.SET_FILE_CHANNELS,
  channels,
});

export const insertFiles =
  (files: FileResponse[]): AppThunkAction =>
  (dispatch, getState) => {
    const { selectedChannel, items: currentFiles } = getState().files;

    if (!selectedChannel) return;
    const { id: selectedChannelId } = selectedChannel;
    const { ownerId: filesOwnerId } = files.at(0) ?? {};

    // todo: use sets once the get files api is updated to use models,
    // currently the list files endpoint returns identityId for the files which
    // is not included in the create file response (which uses models)
    const idsPresent = currentFiles.map((file) => file.id);

    const newFiles = files.filter(
      // check for duplicates and add only the files that are not present
      (file) => idsPresent.indexOf(file.id) === -1,
    );

    // dispatch action to update files if the new files are belong to the currently selected channel
    if (selectedChannelId === filesOwnerId) {
      dispatch({
        type: FilesActionTypes.INSERT_FILES,
        payload: newFiles,
      });
    }
  };

export const updateFileDragging =
  (newState: boolean): AppThunkAction =>
  (dispatch) => {
    dispatch({
      type: FilesActionTypes.UPDATE_FILE_DRAGGING,
      payload: newState,
    });
  };

export const updateHoveredRowNode =
  (rowNode: RowNode | null): AppThunkAction =>
  (dispatch) => {
    dispatch({
      type: FilesActionTypes.UPDATE_HOVERED_ROW_NODE,
      payload: rowNode,
    });
  };

/**
 * Action to set the file menu action state which will open
 * trigger an event in the files table
 * @param newState what is the new file action to perform
 * @returns
 */
export const updateFilesHelperAction =
  (newState: string | null): AppThunkAction =>
  (dispatch) => {
    dispatch({
      type: FilesActionTypes.UPDATE_FILES_HELPER_ACTION,
      payload: newState,
    });
  };

/**
 * Action to update file channel members
 * @param fileChannel
 * @param updatedMemberIds
 * @returns
 */
export const updateFileChannelMembers =
  (fileChannel: FileChannel, updatedMemberIds: string[]): AppThunkAction =>
  async (dispatch) => {
    const memberRecord: Record<string, boolean> = {};
    updatedMemberIds.forEach((id) => {
      memberRecord[id] = true;
    });

    const updatedChannel: FileChannel = {
      ...fileChannel,
      additionalFields: {
        ...fileChannel.additionalFields,
        memberIDs: memberRecord,
      },
    };

    const result = await CallApiWithNotification({
      executeFunction: FilesClient.updateFileChannel,
      params: updatedChannel,
      successMessage: `Group members updated`,
      errorMessage: `Group members could not be updated.`,
      dispatch,
    });

    if (result.status) {
      dispatch(updateFileChannelsAction([result.data]));
    }
  };

// Drives a single batch download from the current selection. Single leaf-file
// selections take the existing per-file download path (no zip); everything else
// goes through the backend batch-download job and the caller-supplied progress
// poller (the hook can't be invoked here, so the component injects it).
export const batchDownload =
  (
    startBatchZipDownload: (jobKey: string, numFiles: number) => void,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    const { selectedRowIds, items, selectedChannel } = getState().files;
    if (selectedRowIds.size === 0) return;

    const selectedItems = items.filter((item) => selectedRowIds.has(item.id));
    if (selectedItems.length === 0) return;

    const folders = selectedItems.filter(
      (item) => item.fields.fileKey === FOLDER_FILE_KEY,
    );
    const files = selectedItems.filter(
      (item) => item.fields.fileKey !== FOLDER_FILE_KEY,
    );

    if (folders.length === 0 && files.length === 1) {
      const file = files.at(0);
      if (!file) return;
      try {
        const fileUrlFromS3 = (
          await S3Utils.getFile(file.fields.fileKey, {
            identityId: file.identityId,
            level: 'protected',
          })
        ).toString();
        dispatch(
          downloadFiles([
            {
              id: file.id,
              fileUrl: fileUrlFromS3,
              fileKey: file.fields.fileKey,
              fileName: file.fields.fileName || '',
              emitAuditEvent: true,
              channelId: selectedChannel?.id,
              path: file.fields.path,
            },
          ]),
        );
        dispatch(clearSelection());
      } catch (e) {
        const { message } = ensureApiError(e);
        dispatch(alertSnackbar({ errorMessage: message }));
      }
      return;
    }

    try {
      const jobKey = await FilesClient.batchDownload({
        fileIds: files.map((f) => f.id),
        folderIds: folders.map((f) => f.id),
      });
      startBatchZipDownload(jobKey, selectedItems.length);
      dispatch(clearSelection());
    } catch (e) {
      const { message } = ensureApiError(e);
      dispatch(
        alertSnackbar({
          errorMessage:
            message ||
            'Your download failed. Please try again or contact support.',
        }),
      );
    }
  };
